(() => {
  const init = (scope = document) => {
    scope.querySelectorAll('[data-bc-reviews]').forEach((root) => {
      if (root.dataset.initialized) return;
      const stage = root.querySelector('[data-review-stage]');
      const slides = Array.from(root.querySelectorAll('[data-review-slide]'));
      if (!stage || !slides.length) return;
      root.dataset.initialized = 'true';
      const dots = Array.from(root.querySelectorAll('[data-review-dot]'));
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const cleanup = [];
      let index = 0;
      let animations = [];
      let progress;
      let visible = false;
      let hovered = false;
      let pointer;
      const listen = (node, type, fn) => {
        node.addEventListener(type, fn);
        cleanup.push(() => node.removeEventListener(type, fn));
      };
      const height = () => { stage.style.height = `${slides[index].offsetHeight}px`; };
      // The pill and autoplay share one 10 s timeline, so pausing either never
      // lets the indicator drift from the actual slide change.
      const syncPlayback = () => {
        if (motion.matches || slides.length < 2) {
          progress?.cancel(); progress = null;
          root.dataset.reviewAutoplay = 'false';
          return;
        }
        if (!progress) {
          const fill = dots[index]?.querySelector('[data-review-progress]');
          if (!fill) return;
          const clock = fill.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], {
            duration: 10000, easing: 'linear', fill: 'forwards'
          });
          clock.pause();
          progress = clock;
          clock.finished.then(() => { if (progress === clock) show(index + 1, false); }).catch(() => {});
        }
        const active = visible && !document.hidden && !hovered && !pointer && !root.querySelector(':focus-visible');
        root.dataset.reviewAutoplay = String(active);
        if (active) progress.play();
        else progress.pause();
      };
      const resetProgress = () => {
        progress?.cancel(); progress = null;
        syncPlayback();
      };
      const show = (next, announce = true) => {
        next = (next + slides.length) % slides.length;
        if (next === index) { resetProgress(); return; }
        const previous = slides[index];
        animations.forEach((animation) => animation.cancel());
        animations = [];
        slides.forEach((slide) => { slide.hidden = slide !== previous; });
        index = next;
        root.dataset.reviewIndex = String(index);
        const current = slides[index];
        current.hidden = false;
        slides.forEach((slide, i) => {
          slide.inert = i !== index;
          slide.setAttribute('aria-hidden', String(i !== index));
        });
        dots.forEach((dot, i) => {
          if (i === index) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        height();
        if (!motion.matches && current.animate) {
          const outgoing = previous.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 220, fill: 'both', easing: 'ease-out' });
          const incoming = current.animate([{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 420, fill: 'both', easing: 'cubic-bezier(.22,1,.36,1)' });
          animations = [outgoing, incoming];
          outgoing.finished.then(() => { if (previous !== slides[index]) previous.hidden = true; }).catch(() => {});
          incoming.finished.then(() => incoming.cancel()).catch(() => {});
        } else previous.hidden = true;
        if (announce) root.querySelector('[data-review-status]').textContent = `${index + 1} ${root.dataset.localizedOf} ${slides.length}: ${current.dataset.reviewBrand}`;
        resetProgress();
      };
      slides.forEach((slide, i) => { slide.hidden = i !== 0; slide.inert = i !== 0; slide.setAttribute('aria-hidden', String(i !== 0)); });
      root.dataset.reviewIndex = '0';
      root.classList.add('is-ready');
      height();
      const controls = root.querySelector('[data-review-controls]');
      if (controls) controls.hidden = false;
      if (slides.length > 1) {
        dots.forEach((dot, i) => listen(dot, 'click', () => show(i)));
        listen(root.querySelector('.bc-reviews__carousel'), 'keydown', (event) => {
          if (event.target.closest('a,input,textarea,select')) return;
          if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
            event.preventDefault(); show(index + (event.key === 'ArrowRight' ? 1 : -1));
          }
          if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); show(event.key === 'Home' ? 0 : slides.length - 1); }
        });
        listen(root, 'pointerenter', (event) => { if (event.pointerType === 'mouse') { hovered = true; syncPlayback(); } });
        listen(root, 'pointerleave', (event) => { if (event.pointerType === 'mouse') { hovered = false; syncPlayback(); } });
        listen(root, 'focusin', syncPlayback);
        listen(root, 'focusout', () => queueMicrotask(syncPlayback));
        listen(stage, 'pointerdown', (event) => {
          if (event.pointerType === 'mouse' || event.target.closest('a,button')) return;
          pointer = { x: event.clientX, y: event.clientY };
          syncPlayback();
        });
        listen(stage, 'pointerup', (event) => {
          if (!pointer) return;
          const dx = event.clientX - pointer.x;
          const dy = event.clientY - pointer.y;
          pointer = undefined;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) show(index + (dx < 0 ? 1 : -1));
          else syncPlayback();
        });
        listen(stage, 'pointercancel', () => { pointer = undefined; syncPlayback(); });
      }
      const visibility = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting && entry.intersectionRatio >= .35;
        syncPlayback();
      }, { threshold: [0, .35] });
      visibility.observe(stage);
      listen(document, 'visibilitychange', syncPlayback);
      listen(motion, 'change', syncPlayback);
      const resize = new ResizeObserver(height);
      slides.forEach((slide) => resize.observe(slide));
      listen(document, 'shopify:block:select', (event) => {
        if (!root.contains(event.target)) return;
        const selected = slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (selected !== -1) show(selected, false);
      });
      syncPlayback();
      const unload = (event) => {
        if (!event.target.contains(root)) return;
        progress?.cancel(); progress = null;
        animations.forEach((animation) => animation.cancel());
        resize.disconnect(); visibility.disconnect();
        cleanup.forEach((remove) => remove());
        document.removeEventListener('shopify:section:unload', unload);
      };
      document.addEventListener('shopify:section:unload', unload);
    });
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
